// Dependencies
import React from "react";

// Modules
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { serverUrl, apiEndpoints, assetBase } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import { logCustomEvent } from "../../core/analytics";

export default function DocumentSingle(props) {
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const user = useSelector((state) => state).authReducer.user;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  useEffect(() => {
    loadNode();
    // eslint-disable-next-line
  }, []);

  const loadNode = () => {
    const nid = params.id;

    if (nid) {
      getContent(nid);
    } else {
      setNotFound();
    }
  };

  const setNotFound = () => {
    setErrorStatus(404);
    setErrorMessage("No data");
    setIsError(true);
  };

  const handleError = (_error) => {
    if (_error?.response?.status === 403) {
      setErrorStatus(403);
      setErrorMessage("You do not have access to view this content.");
    } else {
      setErrorStatus(500);
      setErrorMessage("Failed to get data");
    }
    setIsError(true);
  };

  // Opens the private (unpublished) file through token_share — the
  // /system/files-backed draft, so a creator can preview edits before they are
  // published.
  const openPrivateFile = (row) => {
    logCustomEvent("read", row, "Document");
    window.location.href = `${serverUrl}/shared/files/${row.filename}?access_token=${row.file_access_token}`;
  };

  /**
   * @function getContent
   * @description Preview opens the unpublished draft (private /system/files);
   * the default view opens the published copy from the published directory
   * (the signed asset captured in book.json at publish time).
   */
  const getContent = (nid) => {
    if (props.preview) {
      // Unpublished draft — read the live entity (CRUD endpoint), never the
      // published snapshot, so the newest upload is shown.
      getOnlineNode(
        `${apiEndpoints.documents}/${nid}?_format=json`,
        user.access_token,
      )
        .then((response) => {
          const row = response.data;
          if (row && row.field_document) {
            openPrivateFile(row);
          } else {
            setNotFound();
          }
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
      return;
    }

    // Published view — the list row is served from the published book.json, so
    // field_document holds the signed relative `assets/<file>?sig=…` asset path.
    let path = `${apiEndpoints.documents}?nid=${nid}&_format=json`;
    if (hash) {
      path = `${path}&auHash=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        const row = response.data.rows && response.data.rows[0];
        if (row && row.field_document) {
          if (row.field_document.startsWith("http")) {
            // Absolute private URL (no published snapshot yet) — serve the
            // draft via token_share.
            openPrivateFile(row);
          } else {
            // Signed relative asset — resolve off the published directory.
            logCustomEvent("read", row, "Document");
            window.location.href = `${assetBase}/document/${row.id}/${row.id}/${row.field_document}`;
          }
        } else {
          setNotFound();
        }
      })
      .catch(handleError)
      .finally(() => setIsLoading(false));
  };

  if (isLoading) {
    return <SkeletonSingleScreen />;
  }

  if (isError) {
    return <Error status={errorStatus} message={errorMessage} />;
  }

  return null;
}
